import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  addYears,
  subYears,
  setMonth,
  setYear,
  isSameMonth,
  isSameDay,
  isToday,
  parse,
  isValid as isValidDate,
} from "date-fns";

interface DatePickerFieldProps {
  label?: string;
  name: string;
  value?: string; // YYYY-MM-DD
  onChange: (value: string) => void;
  error?: string | null;
  showErrorMessage?: boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

type ViewMode = "days" | "months" | "years";
type Segment = "dd" | "mm" | "yyyy";

export const DatePickerField: React.FC<DatePickerFieldProps> = ({
  label = "",
  name,
  value = "",
  onChange,
  error,
  showErrorMessage = true,
  disabled = false,
  className = "",
  placeholder = "dd-mm-yyyy",
}) => {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("days");
  const [viewDate, setViewDate] = useState(() =>
    value ? parse(value, "yyyy-MM-dd", new Date()) : new Date()
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const ddRef = useRef<HTMLInputElement>(null);
  const mmRef = useRef<HTMLInputElement>(null);
  const yyRef = useRef<HTMLInputElement>(null);

  const selected = value ? parse(value, "yyyy-MM-dd", new Date()) : null;

  // Derive segment values from the controlled value
  const ddVal = selected ? format(selected, "dd") : "";
  const mmVal = selected ? format(selected, "MM") : "";
  const yyVal = selected ? format(selected, "yyyy") : "";

  // Editable segment state (used while typing, before committing)
  const [editDd, setEditDd] = useState<string | null>(null);
  const [editMm, setEditMm] = useState<string | null>(null);
  const [editYy, setEditYy] = useState<string | null>(null);

  // The displayed segment values: use edit state if active, else derived
  const showDd = editDd ?? ddVal;
  const showMm = editMm ?? mmVal;
  const showYy = editYy ?? yyVal;

  // Try to commit a full date when all segments are filled
  const tryCommitDate = useCallback(
    (dd: string, mm: string, yy: string) => {
      if (dd.length === 2 && mm.length === 2 && yy.length === 4) {
        const parsed = parse(`${yy}-${mm}-${dd}`, "yyyy-MM-dd", new Date());
        if (isValidDate(parsed)) {
          onChange(format(parsed, "yyyy-MM-dd"));
          setEditDd(null);
          setEditMm(null);
          setEditYy(null);
        }
      }
    },
    [onChange]
  );

  const focusPrev = (segment: Segment) => {
    if (segment === "yyyy") mmRef.current?.focus();
    else if (segment === "mm") ddRef.current?.focus();
  };

  const focusNext = (segment: Segment) => {
    if (segment === "dd") mmRef.current?.focus();
    else if (segment === "mm") yyRef.current?.focus();
  };

  const handleSegmentKeyDown = (
    segment: Segment,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    const input = e.currentTarget;
    const currentVal = input.value;

    if (e.key === "Backspace" && currentVal === "") {
      focusPrev(segment);
    } else if (e.key === "ArrowLeft" && input.selectionStart === 0) {
      focusPrev(segment);
    } else if (e.key === "ArrowRight" && input.selectionStart === currentVal.length) {
      focusNext(segment);
    } else if (e.key === "-" || e.key === "/") {
      e.preventDefault();
      focusNext(segment);
    }
  };

  const handleDdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 2);
    setEditDd(raw);
    if (raw.length === 2) {
      // Clamp to 01-31
      const num = Math.min(31, Math.max(1, Number.parseInt(raw, 10)));
      const clamped = String(num).padStart(2, "0");
      setEditDd(clamped);
      mmRef.current?.focus();
      tryCommitDate(clamped, editMm ?? mmVal, editYy ?? yyVal);
    }
  };

  const handleMmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 2);
    setEditMm(raw);
    if (raw.length === 2) {
      const num = Math.min(12, Math.max(1, Number.parseInt(raw, 10)));
      const clamped = String(num).padStart(2, "0");
      setEditMm(clamped);
      yyRef.current?.focus();
      tryCommitDate(editDd ?? ddVal, clamped, editYy ?? yyVal);
    }
  };

  const handleYyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 4);
    setEditYy(raw);
    if (raw.length === 4) {
      setEditYy(raw);
      tryCommitDate(editDd ?? ddVal, editMm ?? mmVal, raw);
    }
  };

  // On blur, clamp partial values or reset
  const handleSegmentBlur = () => {
    // Small delay so focus can move to sibling before we reset
    setTimeout(() => {
      if (
        containerRef.current &&
        !containerRef.current.contains(document.activeElement)
      ) {
        // Reset partial edits if focus left entirely
        setEditDd(null);
        setEditMm(null);
        setEditYy(null);
      }
    }, 0);
  };

  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const dropdownWidth = 280;
      setAlignRight(rect.left + dropdownWidth > window.innerWidth - 16);
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    if (open) document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  // Sync viewDate when value changes externally
  useEffect(() => {
    if (value) {
      setViewDate(parse(value, "yyyy-MM-dd", new Date()));
    } else {
      setEditDd(null);
      setEditMm(null);
      setEditYy(null);
      setViewDate(new Date());
    }
  }, [value]);

  const handleSelect = useCallback(
    (day: Date) => {
      onChange(format(day, "yyyy-MM-dd"));
      setOpen(false);
    },
    [onChange]
  );

  const handleToggle = () => {
    if (!disabled) {
      setOpen((prev) => {
        if (!prev) setViewMode("days");
        return !prev;
      });
    }
  };

  const prevMonth = () => setViewDate((d) => subMonths(d, 1));
  const nextMonth = () => setViewDate((d) => addMonths(d, 1));
  const prevYear = () => setViewDate((d) => subYears(d, 1));
  const nextYear = () => setViewDate((d) => addYears(d, 1));

  const yearRangeStart = Math.floor(viewDate.getFullYear() / 12) * 12;
  const prevYearRange = () => setViewDate((d) => subYears(d, 12));
  const nextYearRange = () => setViewDate((d) => addYears(d, 12));

  const handleMonthSelect = (monthIndex: number) => {
    setViewDate((d) => setMonth(d, monthIndex));
    setViewMode("days");
  };

  const handleYearSelect = (year: number) => {
    setViewDate((d) => setYear(d, year));
    setViewMode("months");
  };

  const goToday = () => {
    const today = new Date();
    setViewDate(today);
    handleSelect(today);
  };

  const handleClear = () => {
    onChange("");
    setOpen(false);
  };

  // Build calendar grid
  const monthStart = startOfMonth(viewDate);
  const monthEnd = endOfMonth(viewDate);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const weeks: Date[][] = [];
  let day = calStart;
  while (day <= calEnd) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(day);
      day = addDays(day, 1);
    }
    weeks.push(week);
  }



  return (
    <div
      ref={containerRef}
      className={`form-field ${error ? "form-field--error" : ""} form-field--responsive datepicker ${alignRight ? "datepicker--align-right" : ""} ${className}`}
    >
      {label && (
        <label htmlFor={`${name}-dd`} className="form-field__label">
          {label}
        </label>
      )}

      <div
        className={`datepicker__trigger ${error ? "datepicker__trigger--error" : ""} ${disabled ? "datepicker__trigger--disabled" : ""}`}
      >
        <div
          className="datepicker__segments"
          onClick={(e) => {
            if (!(e.target as HTMLElement).classList.contains("datepicker__segment")) {
              handleToggle();
            }
          }}
        >
          <input
            ref={ddRef}
            id={`${name}-dd`}
            className="datepicker__segment"
            type="text"
            inputMode="numeric"
            placeholder="dd"
            maxLength={2}
            value={showDd}
            disabled={disabled}
            onChange={handleDdChange}
            onKeyDown={(e) => handleSegmentKeyDown("dd", e)}
            onBlur={handleSegmentBlur}
            aria-label="Day"
            autoComplete="off"
          />
          <span className="datepicker__separator">-</span>
          <input
            ref={mmRef}
            className="datepicker__segment"
            type="text"
            inputMode="numeric"
            placeholder="mm"
            maxLength={2}
            value={showMm}
            disabled={disabled}
            onChange={handleMmChange}
            onKeyDown={(e) => handleSegmentKeyDown("mm", e)}
            onBlur={handleSegmentBlur}
            aria-label="Month"
            autoComplete="off"
          />
          <span className="datepicker__separator">-</span>
          <input
            ref={yyRef}
            className="datepicker__segment datepicker__segment--year"
            type="text"
            inputMode="numeric"
            placeholder="yyyy"
            maxLength={4}
            value={showYy}
            disabled={disabled}
            onChange={handleYyChange}
            onKeyDown={(e) => handleSegmentKeyDown("yyyy", e)}
            onBlur={handleSegmentBlur}
            aria-label="Year"
            autoComplete="off"
          />
        </div>
        <button
          type="button"
          className="datepicker__calendar-btn"
          onClick={handleToggle}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label="Open calendar"
          tabIndex={-1}
        >
          <svg className="datepicker__icon" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zM4 8h12v8H4V8z" />
          </svg>
        </button>
      </div>

      {open && (
        <fieldset className="datepicker__dropdown" aria-label="Date picker">
          {viewMode === "days" && (
            <>
              <div className="datepicker__header">
                <button type="button" className="datepicker__nav" onClick={prevMonth} aria-label="Previous month">
                  ‹
                </button>
                <button
                  type="button"
                  className="datepicker__month-year"
                  onClick={() => setViewMode("months")}
                  aria-label="Switch to month view"
                >
                  {format(viewDate, "MMMM yyyy")}
                </button>
                <button type="button" className="datepicker__nav" onClick={nextMonth} aria-label="Next month">
                  ›
                </button>
              </div>

              <div className="datepicker__weekdays">
                {WEEKDAYS.map((d) => (
                  <span key={d} className="datepicker__weekday">{d}</span>
                ))}
              </div>

              <div className="datepicker__grid">
                {weeks.map((week, wi) =>
                  week.map((d, di) => {
                    const inMonth = isSameMonth(d, viewDate);
                    const sel = selected && isSameDay(d, selected);
                    const today = isToday(d);
                    return (
                      <button
                        key={`${wi}-${di}`}
                        type="button"
                        className={[
                          "datepicker__day",
                          !inMonth && "datepicker__day--outside",
                          sel && "datepicker__day--selected",
                          today && !sel && "datepicker__day--today",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => handleSelect(d)}
                        tabIndex={inMonth ? 0 : -1}
                        aria-label={format(d, "EEEE, MMMM d, yyyy")}
                        aria-pressed={!!sel}
                      >
                        {format(d, "d")}
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}

          {viewMode === "months" && (
            <>
              <div className="datepicker__header">
                <button type="button" className="datepicker__nav" onClick={prevYear} aria-label="Previous year">
                  ‹
                </button>
                <button
                  type="button"
                  className="datepicker__month-year"
                  onClick={() => setViewMode("years")}
                  aria-label="Switch to year view"
                >
                  {format(viewDate, "yyyy")}
                </button>
                <button type="button" className="datepicker__nav" onClick={nextYear} aria-label="Next year">
                  ›
                </button>
              </div>

              <div className="datepicker__month-grid">
                {MONTHS.map((m, i) => {
                  const isCurrent = selected?.getMonth() === i && selected?.getFullYear() === viewDate.getFullYear();
                  return (
                    <button
                      key={m}
                      type="button"
                      className={[
                        "datepicker__month-cell",
                        isCurrent && "datepicker__month-cell--selected",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => handleMonthSelect(i)}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {viewMode === "years" && (
            <>
              <div className="datepicker__header">
                <button type="button" className="datepicker__nav" onClick={prevYearRange} aria-label="Previous years">
                  ‹
                </button>
                <span className="datepicker__month-year">
                  {yearRangeStart} – {yearRangeStart + 11}
                </span>
                <button type="button" className="datepicker__nav" onClick={nextYearRange} aria-label="Next years">
                  ›
                </button>
              </div>

              <div className="datepicker__year-grid">
                {Array.from({ length: 12 }, (_, i) => {
                  const yr = yearRangeStart + i;
                  const isCurrent = selected?.getFullYear() === yr;
                  return (
                    <button
                      key={yr}
                      type="button"
                      className={[
                        "datepicker__year-cell",
                        isCurrent && "datepicker__year-cell--selected",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => handleYearSelect(yr)}
                    >
                      {yr}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div className="datepicker__footer">
            <button type="button" className="datepicker__clear-btn" onClick={handleClear}>
              Clear
            </button>
            <button type="button" className="datepicker__today-btn" onClick={goToday}>
              Today
            </button>
          </div>
        </fieldset>
      )}

      {error && showErrorMessage && (
        <div id={`${name}-error`} className="form-field__error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};
